import { combineReducers } from '@reduxjs/toolkit';
import analytics from './analytics';
import ui from './ui';
import user from './user';
import iap from '../lib/iap/iap.redux';
import care from '../game/care/care.redux';
import social from '../game/social/social.redux';
import level from '../game/level/level.redux';
import pet from '../game/pet/pet.redux';
import decoration from '../game/decoration/decoration.redux';
import inventory from '../game/inventory/inventory.redux';
import pinch from '../lib/analytics/pinch/pinch.redux';
import { captureGenericError } from '../lib/sentryCapture';

const slices = {
  analytics,
  ui,
  user,
  pet,
  care,
  social,
  level,
  iap,
  decoration,
  inventory,
  pinch,
};

/**
 * `combineReducers` only warns about an undefined reducer under
 * `NODE_ENV !== 'production'`; otherwise it drops the key and the store is built
 * permanently missing that slice. This file sits in import cycles, so a slice can
 * still be evaluating when it is read here, and which end of a cycle the bundler
 * enters changes with chunk boundaries.
 *
 * Development throws so the cycle is fixed at the source. Production reports it
 * and carries on with the slice missing, rather than crashing at launch.
 */
for (const [name, reducer] of Object.entries(slices)) {
  if (typeof reducer === 'function') continue;

  const error = new Error(
    `Reducer '${name}' was ${typeof reducer} when the store was built. ` +
      `This is an import cycle: something imported '${name}' while it was ` +
      `still evaluating. Break the cycle rather than reordering imports.`,
  );
  if (process.env.IS_DEVELOPMENT) throw error;

  // `Sentry.init` runs at the top level of `src/lib/sentry`, which is part of
  // the same cycle, so it may not have run yet. The static import graph
  // finishes evaluating before any timer fires.
  setTimeout(() => captureGenericError('Store built without a reducer', error));
}

// Not cast: a cast here erases the inferred `RootState`.
export default combineReducers(slices);
